import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  InputAdornment,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import BenefitCard from '../../../v2/Benefits/Beneficiary/Components/BenefitCard';
import FirmDetails from '../../../v2/Benefits/Beneficiary/Components/FirmDetails';
import {
  CustomButton,
  CustomCheckbox,
  CustomRadioGroup,
  StandardChip,
  TextField,
  ToggleSwitch,
  CustomSelect,
} from '../../../v2/MUI_custom_component';
import ReusableModal from '../../../v2/MUI_custom_component/Modal/CustomModal';
import {
  AddShopInsuranceAction,
  getShopInsuranceInfoAction,
  updateShopInsuranceAction,
} from './APIs/action';
import IconReusable from '../../../components/IconReusable';
import { GetAllCityOfState, GetAllStates } from '../../../screen/auth/action/util_actions';
import { getLocationAndPincodeAction } from '../../../v2/Onboarding/auth/action';
import { useDispatch, useSelector } from 'react-redux';

const getFieldComponent = (
  field,
  values,
  setValues,
  disabled = false,
  validationErrors = {},
  shop_details = null,
  shopInfoData = null,
) => {
  if (field.dependsOn) {
    const [depId, depVal] = field.dependsOn.split(':');
    const currentValue = values[depId];
    
    // Handle boolean values - convert both to lowercase strings for comparison
    let normalizedCurrentValue = String(currentValue).toLowerCase();
    let normalizedDepVal = depVal.toLowerCase();
    
    // Convert boolean true/false to string equivalents
    if (currentValue === true) normalizedCurrentValue = 'true';
    if (currentValue === false) normalizedCurrentValue = 'false';
    
    if (!values.hasOwnProperty(depId) || normalizedCurrentValue !== normalizedDepVal) {
      return null;
    }
  }

  const handleChange = (value) => {
    setValues((prev) => {
      const newValues = { ...prev, [field.id]: value };
      if (field.type === 'boolean' && value === null) {
        const clearDependentFields = (changedFieldId) => {
          shop_details?.questions.forEach((section) => {
            section.fields.forEach((depField) => {
              if (
                depField.dependsOn &&
                depField.dependsOn === `${changedFieldId}:true`
              ) {
                newValues[depField.id] = null;
                clearDependentFields(depField.id);
              }
            });
          });
        };
        clearDependentFields(field.id);
      }

      return newValues;
    });
  };

  if (field.id === 'q2') {
    let bankOptions = shopInfoData?.popular_banks_dropdown;
    if (!Array.isArray(bankOptions)) return null;

    if (values[field.id] === 'OTHER') {
      const getPlaceholder = () => 'Enter Bank Name';
      const getSlotProps = () => ({});
      const handleTextFieldChange = (e) => {
        let value = e.target.value;
        if (value.length > 60) {
          value = value.substring(0, 60);
        }
        setValues((prev) => ({ ...prev, q2_other: value }));
      };

      return (
        <>
          <TextField
            key={field.id + '_other'}
            label={'Custom Bank Name'}
            placeholder={getPlaceholder()}
            value={values.q2_other || ''}
            onChange={handleTextFieldChange}
            disabled={disabled}
            error={!!validationErrors.q2_other}
            helperText={validationErrors.q2_other}
            slotProps={getSlotProps()}
            inputProps={{ maxLength: 60 }}
          />
        </>
      );
    } else {
      // Check if the current value exists in bankOptions
      const currentValueExists = bankOptions.some(
        (option) => option.value === values[field.id],
      );

      const selectValue = currentValueExists ? values[field.id] : '';

      // Show only CustomSelect
      return (
        <CustomSelect
          key={field.id}
          label={field.label}
          placeholder='Select Bank'
          value={selectValue}
          onChange={(e) => {
            handleChange(e.target.value);
            if (e.target.value !== 'OTHER') {
              setValues((prev) => ({ ...prev, q2_other: '' }));
            }
          }}
          error={!!validationErrors[field.id]}
          helperText={validationErrors[field.id]}
          fullWidth
          displayEmpty
        >
          {!selectValue && (
            <MenuItem
              value=''
              disabled
            >
              Select Bank
            </MenuItem>
          )}

          {bankOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </CustomSelect>
      );
    }
  }

  switch (field.type) {
    case 'boolean':
      return (
        <ToggleSwitch
          key={field.id}
          label={field.label}
          checked={!!values[field.id]}
          size='large'
          onChange={(e) => handleChange(e.target.checked ? true : null)}
          disabled={disabled}
        />
      );
    case 'textarea':
      return (
        <TextField
          key={field.id}
          label={field.label}
          value={values[field.id] || ''}
          onChange={(e) => handleChange(e.target.value)}
          multiline
          minRows={2}
          disabled={disabled}
          error={!!validationErrors[field.id]}
          helperText={validationErrors[field.id]}
        />
      );
    case 'textfield':
      const getPlaceholder = () => {
        if (field.id === 'q7') return 'Enter Amount';
        return '';
      };

      const getSlotProps = () => {
        if (field.id === 'q7') {
          return {
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <CurrencyRupeeIcon sx={{ fontSize: '24px' }} />
                </InputAdornment>
              ),
            },
          };
        }
        return {};
      };

      const handleTextFieldChange = (e) => {
        let value = e.target.value;
        if (field.id === 'q7') {
          value = value.replace(/[^0-9.]/g, '');

          const parts = value.split('.');
          if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
          }
          if (parts[1] && parts[1].length > 2) {
            value = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }
        handleChange(value);
      };

      return (
        <TextField
          key={field.id}
          label={field.label}
          placeholder={getPlaceholder()}
          value={values[field.id] || ''}
          onChange={handleTextFieldChange}
          disabled={disabled}
          error={!!validationErrors[field.id]}
          helperText={validationErrors[field.id]}
          slotProps={getSlotProps()}
        />
      );
    case 'radio':
      return (
        <Box key={field.id}>
          <Typography
            variant='body2'
            sx={{ mb: 1, color: '#333333' }}
          >
            {field.label}
          </Typography>
          <CustomRadioGroup
            options={field.options}
            value={values[field.id] || ''}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            name={field.id}
          />
        </Box>
      );
    case 'multi-select-chip':
      return (
        <Box key={field.id}>
          <Typography
            variant='body1'
            sx={{ mb: 1, color: '#333333' }}
          >
            {field.label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {field.options.map((option) => (
              <StandardChip
                key={option}
                label={option}
                selected={(values[field.id] || []).includes(option)}
                showDelete={(values[field.id] || []).includes(option)}
                onChange={(isSelected) => {
                  const currentValues = values[field.id] || [];
                  if (isSelected) {
                    handleChange([...currentValues, option]);
                  } else {
                    handleChange(currentValues.filter((v) => v !== option));
                  }
                }}
                captalise={false}
                disabled={disabled}
              />
            ))}
          </Box>
          {validationErrors[field.id] && (
            <Typography
              variant='caption'
              color='error'
              sx={{ mt: 0.5, display: 'block' }}
            >
              {validationErrors[field.id]}
            </Typography>
          )}
        </Box>
      );
    case 'checkbox':
      return (
        <CustomCheckbox
          key={field.id}
          label={field.label}
          checked={!!values[field.id]}
          onChange={(e) => handleChange(e.target.checked)}
          disabled={disabled}
        />
      );
    default:
      return null;
  }
};

const ShopInsurance = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 1200px)');
  const [shopInfoData, setShopInfoData] = useState();
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState({});
  const [loader, setLoader] = useState(false);
  
  
  // Address form states
  const [addressForm, setAddressForm] = useState({
    address_line_1: '',
    address_line_2: '',
    state_id: '',
    state_name: '',
    district_id: '',
    district_name: '',
    location_id: '',
    location_name: '',
    pincode_id: ''
  });
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [allPincodes, setAllPincodes] = useState([]);
  const [addressValidationErrors, setAddressValidationErrors] = useState({});
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [addressFilled, setAddressFilled] = useState(false);
  const [isAddAddressModalOpen, setIsAddAddressModalOpen] = useState(false);
  const [firstSelection, setFirstSelection] = useState(null);

  const {
    benefit_card,
    selected_firm,
    shop_details,
    confirmation_text,
    popular_banks_dropdown,
  } = shopInfoData || {};
  const {
    isEdit,
    shop_data: shopData,
    insurance_type: insurance_type,
    address_details,
    address_editable,
    edit_address
  } = location?.state || {};
  const {
    selectedEntity: selectedCompanyEntity,
  } = useSelector(
    (state) => state.entityReducer
  );
  useEffect(() => {
    if (isEdit && shopData) {
      const initialValues = {};
      shopData.forEach((section) => {
        section.fields?.forEach((field) => {
          if (field.id && field.value != null) {
            let value = field.value;
            
            // Convert string booleans to actual booleans for proper handling
            if (field.type === 'boolean') {
              if (value === 'True' || value === true) {
                value = true;
              } else if (value === 'False' || value === false) {
                value = false;
              } else {
                value = null;
              }
            }
            
            initialValues[field.id] = value;
          }
        });
      });
      setFormValues(initialValues);
    }
  }, [isEdit, shopData]);

  useEffect(() => {
    if (shopInfoData?.popular_banks_dropdown && formValues.q2) {
      const bankOptions = shopInfoData.popular_banks_dropdown;
      const currentValueExists = bankOptions.some(
        (option) => option.value === formValues.q2,
      );

      if (!currentValueExists && formValues.q2 !== 'OTHER') {
        setFormValues((prev) => ({
          ...prev,
          q2: 'OTHER',
          q2_other: prev.q2,
        }));
      }
    }
  }, [shopInfoData?.popular_banks_dropdown, formValues.q2]);

  const validateDependentFields = () => {
    const errors = {};

    shop_details?.questions.forEach((section) => {
      section.fields.forEach((field) => {
        const shouldValidate = checkFieldValidation(field, section);

        if (shouldValidate) {
          const fieldValue = formValues[field.id];
          const isEmpty = checkFieldEmpty(field, fieldValue);

          if (isEmpty) {
            errors[field.id] = `${field.label} is required`;
          }
        }
      });
    });

    if (formValues.q2 === 'OTHER') {
      const q2OtherValue = formValues.q2_other;
      if (!q2OtherValue || q2OtherValue.trim() === '') {
        errors.q2_other = ' Bank Name is required';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const checkFieldValidation = (field, section) => {
    if (field.dependsOn) {
      const [depId, depVal] = field.dependsOn.split(':');
      const currentValue = formValues[depId];
      
      // Handle boolean values - convert both to lowercase strings for comparison
      let normalizedCurrentValue = String(currentValue).toLowerCase();
      let normalizedDepVal = depVal.toLowerCase();
      
      // Convert boolean true/false to string equivalents
      if (currentValue === true) normalizedCurrentValue = 'true';
      if (currentValue === false) normalizedCurrentValue = 'false';
      
      return formValues.hasOwnProperty(depId) && normalizedCurrentValue === normalizedDepVal;
    }
    return false;
  };
  const checkFieldEmpty = (field, fieldValue) => {
    if (field.type === 'textfield' || field.type === 'textarea') {
      return !fieldValue || fieldValue.trim() === '';
    }
    if (field.type === 'boolean' || field.type === 'radio') {
      return fieldValue === null || fieldValue === undefined;
    }
    return false;
  };
  const getSectionsToDisplay = () => {
    return currentStep === 1
      ? shop_details?.questions.slice(0, 2)
      : shop_details?.questions.slice(2);
  };
  const handleSubmit = async () => {
    setValidationErrors({});
    if (!validateDependentFields()) return;
    if((!isEdit && !validateAddressForm())) {
      toast.error('Please fill the Shop Address')
      return
    };
    
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }
    if (!confirm) {
      setValidationErrors({ confirm: 'Please confirm the details to proceed' });
      return;
    }
    await submitForm();
  };
  const getShopInsuranceInfo = async () => {
    setLoader(true);
    const { data, error, message } = await getShopInsuranceInfoAction(selectedCompanyEntity);
    if (!error) {
      setShopInfoData(data);
    } else {
      toast.error(message);
    }
    setLoader(false);
  };
  const handleGoBack = () => setCurrentStep(1);
  const clearConfirmationError = () => {
    if (validationErrors.confirm) {
      setValidationErrors((prev) => {
        const { confirm, ...rest } = prev;
        return rest;
      });
    }
  };
  // Address Details Handlers
  const getStates = async () => {
    setIsLoadingStates(true);
    const { data, error, message } = await GetAllStates();
    setIsLoadingStates(false);
    if (!error) {
      setStates(
        data.map((state) => ({
          label: state.state_name,
          value: state.id,
        })),
      );
    } else {
      return toast.error(message);
    }
  };
  const getDistricts = async (stateId) => {
    setDistricts([]);
    setIsLoadingDistricts(true);
    const { data, error, message } = await GetAllCityOfState(stateId);
    setIsLoadingDistricts(false);
    if (!error) {
      setDistricts(
        data.map((district) => ({
          label: district.district_name,
          value: district.id,
        })),
      );
    } else return toast.error(message);
  };
  const getlocationPincode = async (
    districtId,
    locationId = null,
    PincodeId = null,
  ) => {
    const { data, error } = await getLocationAndPincodeAction(districtId, {
      location_id: locationId,
      pincode_id: PincodeId,
    });
    if (!error) {
      if (locationId && !addressForm.pincode) {
        setAllPincodes(
          data.pincode_list.map((pincode) => ({
            label: pincode.pincode_name,
            value: pincode.pincode_id,
          })),
        );
      } else if (PincodeId && !addressForm.city) {
        setAllCities(
          data.location_list.map((city) => ({
            label: city.location_name,
            value: city.location_id,
          })),
        );
      } else if (!locationId && !PincodeId) {
        setAllPincodes(
          data.pincode_list.map((pincode) => ({
            label: pincode.pincode_name,
            value: pincode.pincode_id,
          })),
        );
        setAllCities(
          data.location_list.map((city) => ({
            label: city.location_name,
            value: city.location_id,
          })),
        );
      }
    }
  };
  const handleEditClick = () => {
    setIsAddAddressModalOpen(true);
    setAddressValidationErrors({});
    const details = (isEdit && address_editable) ? address_details.details : addressForm ;
    setAddressForm({
      address_line_1: details.address_line_1 || '',
      address_line_2: details.address_line_2 || '',
      state_id: details.state_id || '',
      state_name: details.state_name || '',
      district_id: details.district_id || '',
      district_name: details.district_name || '',
      location_id: details.location_id || '',
      location_name: details.location_name || '',
      pincode_id: details.pincode_id || ''
    });
    if (details.state_id) {
      getDistricts(details.state_id);
    }
    if (details.district_id) {
      getlocationPincode(details.district_id);
    }
  };
  const handleAddressFormChange = (field, value) => {
    setAddressForm(prev => ({ ...prev, [field]: value }));
    if (addressValidationErrors[field]) {
      setAddressValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (field === 'state_id') {
      setAddressForm(prev => ({ ...prev, district_id: '', district_name: '', location_id: '', location_name: '', pincode_id: '' }));
      setDistricts([]);
      setAllCities([]);
      setAllPincodes([]);
      if (value) {
        const selectedState = states.find(state => state.value === value);
        setAddressForm(prev => ({ ...prev, state_name: selectedState?.label || '' }));
        getDistricts(value);
      }
    } else if (field === 'district_id') {
      setAddressForm(prev => ({ ...prev, location_id: '', location_name: '', pincode_id: '' }));
      setAllCities([]);
      setFirstSelection(null);
      setAllPincodes([]);
      if (value) {
        const selectedDistrict = districts.find(district => district.value === value);
        setAddressForm(prev => ({ ...prev, district_name: selectedDistrict?.label || '' }));
        getlocationPincode(value);
        
      }
    } else if (field === 'location_id') {
      if (value) {
        if (!firstSelection) setFirstSelection('location');
        const selectedLocation = allCities.find(city => city.value === value);
        setAddressForm(prev => ({ ...prev, location_name: selectedLocation?.label || '', ...(firstSelection === 'location' ? { pincode_id: '' } : {}), }));
        getlocationPincode(addressForm.district_id, value, null);
      }
    }
    else if (field === 'pincode_id') {
      if (value) {
        if (!firstSelection) setFirstSelection('pincode');
        const selectedPincode = allPincodes.find(pincode => pincode.value === value);
        setAddressForm(prev => ({ ...prev, pincode_id: selectedPincode?.label || '' ,
          ...(firstSelection === 'pincode'
          ? {
              location_id: '',
              location_name: '',
            }
          : {}),
        }));
        getlocationPincode(addressForm.district_id, null, value);
      }
    }
  };
  const validateAddressForm = () => {
    const errors = {};
    if (!addressForm.address_line_1.trim()) errors.address_line_1 = 'Address Line 1 is required';
    if (!addressForm.address_line_2.trim()) errors.address_line_2 = 'Address Line 2 is required';
    if (!addressForm.state_id) errors.state_id = 'State is required';
    if (!addressForm.district_id) errors.district_id = 'District is required';
    if (!addressForm.location_id) errors.location_id = 'City is required';
    if (!addressForm.pincode_id) errors.pincode_id = 'PIN Code is required';   
    setAddressValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleAddressSubmit = () => {
    if (!validateAddressForm()) {
      return;
    }
    dispatch({
      type: 'SET_SHOP_ADDRESS',
      data: addressForm,
    });
    setIsAddAddressModalOpen(false);
    setAddressFilled(true);
  };
  const submitForm = async () => {
    setIsLoading(true);
    const payload = createPayload();
    const { error, message } = isEdit
      ? await updateShopInsuranceAction(payload)
      : await AddShopInsuranceAction(payload);
    setIsLoading(false);
    if (!error) {
      toast.success(message);
      navigate('/beneficiary/shop-details', { state: { insurance_type, selectedCompanyEntity } });
    } else {
      toast.error(message);
    }
  };
  const createPayload = () => {
    const payload = shop_details?.questions.reduce((acc, section) => {
      const validFields = section.fields.filter((field) => {
        if (field.dependsOn) {
          const [depId, depVal] = field.dependsOn.split(':');
          const currentValue = formValues[depId];
          
          // Handle boolean values - convert both to lowercase strings for comparison
          let normalizedCurrentValue = String(currentValue).toLowerCase();
          let normalizedDepVal = depVal.toLowerCase();
          
          // Convert boolean true/false to string equivalents
          if (currentValue === true) normalizedCurrentValue = 'true';
          if (currentValue === false) normalizedCurrentValue = 'false';
          
          return (
            formValues.hasOwnProperty(depId) &&
            normalizedCurrentValue === normalizedDepVal
          );
        }
        return true;
      });
      acc[section.section_id] = validFields.map((field) => {
        let value = formValues[field.id] ?? field.value;
        if (field.id === 'q2' && value === 'OTHER') {
          value = formValues.q2_other || '';
        }
        if (field.id === 'q7' && value) {
          value = Number(value);
        }
        return {
          id: field.id,
          label: field.label,
          value,
        };
      });
      return acc;
    }, {});
    payload.company_esi_id = selectedCompanyEntity;
    if (addressFilled || (isEdit && addressFilled)) {
      const addressData = addressForm;
      if (addressData) {
        payload.address_details = {
          address_line_1: addressData.address_line_1,
          address_line_2: addressData.address_line_2,
          state_id: addressData.state_id,
          district_id: addressData.district_id,
          location_id: addressData.location_id,
          pincode_id: addressData.pincode_id
        };
      }
    }else{
      payload.address_details = null;
    }
    return payload;
  };
  useEffect(() => {
    getShopInsuranceInfo();
    
  }, []);
  useEffect(() => {
    if(edit_address){
      handleEditClick();
    };
  }, [edit_address]);
  useEffect(() => {
    if (isAddAddressModalOpen) {
      setAddressValidationErrors({});
      if (states.length === 0) {
        getStates();
      }
    }
  }, [isAddAddressModalOpen]);

  if (loader) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Typography
          variant='body2'
          color='text.secondary'
        >
          Loading...
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: 'flex', flexDirection: 'column' , gap: 2, justifyContent: 'space-between'}}
        >
          <BenefitCard
            item={benefit_card}
            // sx={{ height: '100%' }}
          />
          {/* Add Address Button Section */}
          {!(addressFilled || !!(address_details && isEdit)) && !isMobile &&(
            <CustomButton
              variant='filled'
              color='primary'
              onClick={() => setIsAddAddressModalOpen(true)}
              label='Add Address'
              fullWidth
            />
          )}
        </Grid>
        
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Box
            sx={{
              background: '#fff',
              p: 2,
              borderRadius: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
            }}
          >
            {selected_firm?.length > 0 ? (
              selected_firm.map((detail, index) => (
                <FirmDetails
                  key={detail?.id || index}
                  detail={detail}
                />
              ))
            ) : (
              <Typography
                variant='body2'
                color='text.secondary'
              >
                No firm details available
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          sm={6}
        >
          {(addressFilled || !!(address_details && isEdit)) ? (
            <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#fff', display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Typography variant="h2">
                Shop Address
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                <Typography variant="body1">
                  { ((isEdit) && !addressFilled) ?
                    `${address_details?.details?.address_line_1 || ''} ${address_details?.details?.address_line_2 || ''}, ${address_details?.details?.district_name || ''}, ${address_details?.details?.location_name || ''}, ${address_details?.details?.state_name || ''} - ${address_details?.details?.pincode_id || ''}`
                    :
                    `${addressForm?.address_line_1} ${addressForm?.address_line_2}, ${addressForm?.district_name || ''}, ${addressForm?.location_name || ''}, ${addressForm?.state_name || ''} - ${addressForm?.pincode_id || ''}`
                  }
                </Typography>
                {(address_editable || addressFilled) && (
                  <Box sx={{
                    cursor: 'pointer', 
                    display: 'flex',
                    width: '24px',
                    height: '24px', 
                    justifyContent: 'center'	
                  }}
                  onClick={handleEditClick}
                  >
                    <IconReusable
                      name="Edit"
                      size={isMobile ? 16 : 24}
                      color="#995A00"
                    />
                  </Box>
                )}
              </Box>
            </Box>
          ): (
            <CustomButton
              variant='filled'
              color='primary'
              onClick={() => setIsAddAddressModalOpen(true)}
              label='Add Address'
              fullWidth
              sx={{ mb: 2, display: {xs: 'block', md: 'none'} }}
            />
          )}
          </Grid>
        </Grid>
      <Grid
        container
        spacing={4}
      >
        <Grid
          item
          xs={12}
        >
          <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant='h2'>{shop_details?.header_title}</Typography>
            </Box>
            <Box>
              {getSectionsToDisplay()?.map((section) => {
                return (
                  <Box
                    key={section.section}
                    mb={4}
                  >
                    <Typography
                      variant='labelD'
                      sx={{ mb: 1 }}
                    >
                      {section.section}
                    </Typography>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                    >
                      {section.fields.map((field) => (
                        <React.Fragment key={field.id}>
                          {getFieldComponent(
                            field,
                            formValues,
                            setFormValues,
                            false,
                            validationErrors,
                            shop_details,
                            shopInfoData,
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {currentStep === 2 && (
              <Box>
                <CustomCheckbox
                  label={confirmation_text.label}
                  onChange={(e) => {
                    setConfirm(e.target.checked);
                    if (e.target.checked) clearConfirmationError();
                  }}
                />
                {validationErrors.confirm && (
                  <Typography
                    variant='caption'
                    color='error'
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {validationErrors.confirm}
                  </Typography>
                )}
              </Box>
            )}
            <Box
              mt={2}
              sx={{
                display: currentStep === 2 ? 'flex' : 'block',
                justifyContent: 'space-between',
              }}
            >
              {currentStep === 2 && (
                <CustomButton
                  variant='outlined'
                  color='primary'
                  onClick={handleGoBack}
                  disabled={isLoading}
                  label='Back'
                  sx={{ width: { xs: '48%', sm: 'auto' } }}
                />
              )}

              <CustomButton
                variant='filled'
                color='primary'
                onClick={handleSubmit}
                disabled={isLoading}
                label={
                  isLoading
                    ? 'Submitting...'
                    : currentStep === 1
                    ? 'Save & Continue'
                    : 'Save'
                }
                sx={{
                  width: {
                    xs: '48%',
                    sm: 'auto',
                  },
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Add Address Modal */}
      <ReusableModal
        open={isAddAddressModalOpen}
        onClose={()=>setIsAddAddressModalOpen(false)}
        title="Add Shop Address"
        maxWidth="800px"
        centerAlign
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap: 1}}>
            <IconReusable name="InfoOutlined" size={14} color="#CC0000" /> 
            <Typography variant="p3" sx={{ color: '#CC0000' }}>
              Provided address should be registered with government authorities.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1}}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12}>
                <TextField
                  label="Address line 1*"
                  fullWidth
                  value={addressForm.address_line_1}
                  onChange={(e) => handleAddressFormChange('address_line_1', e.target.value)}
                  error={!!addressValidationErrors.address_line_1}
                  helperText={addressValidationErrors.address_line_1}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  label="Address line 2"
                  fullWidth
                  value={addressForm.address_line_2}
                  onChange={(e) => handleAddressFormChange('address_line_2', e.target.value)}
                  error={!!addressValidationErrors.address_line_2}
                  helperText={addressValidationErrors.address_line_2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  label="State*"
                  fullWidth
                  value={addressForm.state_id}
                  onChange={(e) => handleAddressFormChange('state_id', e.target.value)}
                  error={!!addressValidationErrors.state_id}
                  helperText={addressValidationErrors.state_id}
                  displayEmpty
                  disabled={isLoadingStates}
                >
                  <MenuItem value="">
                    {isLoadingStates ? 'Loading states...' : 'Select State'}
                  </MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.value} value={state.value}>
                      {state.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  label="District*"
                  fullWidth
                  value={addressForm.district_id}
                  onChange={(e) => handleAddressFormChange('district_id', e.target.value)}
                  error={!!addressValidationErrors.district_id}
                  helperText={addressValidationErrors.district_id}
                  displayEmpty
                  disabled={!addressForm.state_id || isLoadingDistricts}
                >
                  <MenuItem value="">
                      {!addressForm.state_id 
                        ? 'Select state first' 
                        : isLoadingDistricts 
                        ? 'Loading districts...' 
                        : 'Select District'
                      }
                  </MenuItem>
                  {districts.map((district) => (
                    <MenuItem key={district.value} value={district.value}>
                      {district.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  label="City*"
                  fullWidth
                  value={addressForm.location_id}
                  onChange={(e) => handleAddressFormChange('location_id', e.target.value)}
                  error={!!addressValidationErrors.location_id}
                  helperText={addressValidationErrors.location_id}
                  displayEmpty
                  disabled={!addressForm.district_id || isLoadingCities}
                >
                  <MenuItem value="">
                      {isLoadingCities 
                        ? 'Loading cities...' 
                        : 'Select City'
                      }
                  </MenuItem>
                  {allCities.map((city) => (
                    <MenuItem key={city.value} value={city.value}>
                      {city.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} sm={6}>  
                <CustomSelect
                  label="Pincode*"
                  fullWidth
                  value={addressForm.pincode_id}
                  onChange={(e) => handleAddressFormChange('pincode_id', e.target.value)}
                  error={!!addressValidationErrors.pincode_id}
                  helperText={addressValidationErrors.pincode_id}
                  displayEmpty
                  disabled={allPincodes.length === 0}
                >
                  <MenuItem value="">
                      { allPincodes.length === 0
                        ? 'No pincodes available'
                        : 'Select PIN Code'
                      }
                  </MenuItem>
                  {allPincodes.map((pincode) => (
                    <MenuItem key={pincode.value} value={pincode.value}>
                      {pincode.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <CustomButton
              variant="filled"
              label="Save Address"
              onClick={handleAddressSubmit}
              fullWidth
            />
          </Box>
        </Box>
      </ReusableModal>
    </Box>
  );
};

export default ShopInsurance;
